// load promise polyfill for IE 11 conditional import.
import 'core-js/stable/promise';

import { isIE11 } from './polyfills/utils';

import React, { ReactElement } from 'react';
import ReactDOM from 'react-dom';

import * as serviceWorker from './serviceWorker';
import axeConfig from './axeConfig';

import indexConfig from './indexConfig';
import { LiveChatNotificationApiSettingsBase } from '@alme/api-ts/LiveChat/LiveChatNotificationApiSettingsBase';
import IvaWrapper from './IvaWrapper';
import { IvaWrapperProps } from './IvaWrapper';
import { defaultAccessKey, getPersonaFromMapping } from './utils/PersonaUtils';
import { PersonaState } from './models/AlmePersona';
import appConfigMain from './personas/mainPersona/config';
import appThemeMain from './personas/mainPersona/theme';
import { VerintGlobal } from './models/VerintWindow';
import { mapClientConfig } from './utils/ConfigMapUtils';

declare let window: VerintGlobal;

export async function renderAlmeUi(props?: IvaWrapperProps): Promise<void> {
    await loadPolyfills();

    try {
        await setupReactAxe();

        const almeUiRoot = document.getElementById(indexConfig.embeddedIvaRootId);
        const almePopupRoot = document.getElementById(indexConfig.popupIvaRootId);
        const testPageRoot = document.getElementById(indexConfig.testPageRootId);
        const clientDiv = document.getElementById(indexConfig.inlineIvaRootId);

        if (props?.verintAccessKey) {
            await getInitialPersona(props);
        }

        let almeApp: ReactElement;
        let rootElement: HTMLElement;

        if (almeUiRoot) {
            almeApp = (<IvaWrapper {...props} ></IvaWrapper>);
            rootElement = almeUiRoot;
        } else if (almePopupRoot) {
            almeApp = (<IvaWrapper {...props} ivaType='popup'></IvaWrapper>);
            rootElement = almePopupRoot;
        } else if (testPageRoot) {
            almeApp = (<IvaWrapper {...props} renderTestPage={true}></IvaWrapper>);
            rootElement = testPageRoot;
        } else if (clientDiv) {
            almeApp = (<IvaWrapper {...props} ivaType='inline'></IvaWrapper>);
            rootElement = clientDiv;
        } else {
            almeApp = (<IvaWrapper {...props}></IvaWrapper>);
            rootElement = setupRootDiv();
        }

        // TODO: Enable strict mode in MUI v5.
        const strictAlmeApp = almeApp;
        // (
        //     <React.StrictMode>
        //         {almeApp}
        //     </React.StrictMode>
        // );
        ReactDOM.render(strictAlmeApp, rootElement);
    } catch (ex) {
        console.error('Failed to render Alme UI', ex);
    }
}

async function renderIVA(props?: IvaWrapperProps): Promise<void> {
    await loadPolyfills();

    try {
        await setupReactAxe();
        if (props?.clientConfig) {
            props.almeConfig = mapClientConfig(props.clientConfig);
        }
        const almeApp = (<IvaWrapper {...props}></IvaWrapper>);
        const ivaProDiv = document.getElementById(indexConfig.ivaProDivId) || setupRootDiv();

        ReactDOM.render(almeApp, ivaProDiv);
    } catch (ex) {
        console.error('Failed to render Alme UI', ex);
    }
}

async function setupReactAxe() {
    if (process.env.NODE_ENV !== 'production') {
        const axe = (await import('react-axe')).default;
        await axe(React, ReactDOM, 1000, axeConfig, document);
    }
}

function setupRootDiv() {
    const mainElements = document.getElementsByTagName('main');
    let bodyElement: HTMLElement;
    if (mainElements.length) {
        bodyElement = mainElements[mainElements.length - 1];
    } else {
        bodyElement = document.body;
    }
    const almeDiv = document.createElement('div');
    almeDiv.setAttribute('id', indexConfig.embeddedIvaRootId);
    bodyElement.appendChild(almeDiv);
    return almeDiv;
}

async function loadPolyfills() {
    try {
        if (isIE11()) {
            await import('./polyfills/polyfills');
        }
    } catch (error) {
        console.error('Failed to load IE 11 polyfills', error);
    }
}

async function getInitialPersona(props: IvaWrapperProps) {
    const defaultPersona = {
        config: props?.almeConfig || appConfigMain,
        theme: props?.theme || appThemeMain,
        themeMode: props?.themeMode || 'light',
        verintAccessKey: { current: defaultAccessKey },
    } as PersonaState;
    const persona = await getPersonaFromMapping(props?.verintAccessKey || defaultAccessKey, defaultPersona);
    props.theme = persona.theme;
    props.almeConfig = persona.config;
    props.themeMode = persona.themeMode;
}

export function createApiSettings(almeApiHostname: string): LiveChatNotificationApiSettingsBase {
    const apiSettings = new LiveChatNotificationApiSettingsBase();
    apiSettings.AlmeApiHostname = almeApiHostname;
    return apiSettings;
}


try {
    if (!window.Verint) {
        window.Verint = {
            createApiSettings,
            renderIVA,
        };
    }
    const ivaProDiv = document.getElementById(indexConfig.ivaProDivId);
    if (!ivaProDiv) {
        void renderAlmeUi({});
    }

    // If you want your app to work offline and load faster, you can change
    // unregister() to register() below. Note this comes with some pitfalls.
    // Learn more about service workers: https://bit.ly/CRA-PWA
    serviceWorker.unregister();
} catch (ex) {
    console.error('Failed to add alme root element', ex);
}
